import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCalendarAlt, faTimes, faEye } from '@fortawesome/free-solid-svg-icons';
import {
  fetchRepartos,
  fetchRefreshRepartos,
  confirmarEntrega,
  prorrogarEntrega,
  rechazarEntrega,
} from '../../../../redux/features/repartos';
import API from '../../../../services/api';
import useRutas from '../shared/hooks/useRutas';


const ESTADO_BADGES = {
  'En reparto': { bg: '#FFF3E0', color: '#E65100', label: 'En reparto' },
  'Pendiente reparto': { bg: '#E3F2FD', color: '#1565C0', label: 'Pendiente reparto' },
  'Pendiente preparar': { bg: '#F5F5F5', color: '#616161', label: 'Pendiente preparar' },
  'Entregado': { bg: '#E8F5E9', color: '#2E7D32', label: 'Entregado' },
  'Rechazado': { bg: '#FFEBEE', color: '#C62828', label: 'Rechazado' },
};

function EstadoBadge({ estado }) {
  const cfg = ESTADO_BADGES[estado] || { bg: '#F5F5F5', color: '#616161', label: estado };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      fontSize: '0.72rem', fontWeight: 700,
      padding: '3px 10px', borderRadius: 99,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function KpiCard({ label, value, icon: Icon, color, bg }) {
  return (
    <div className="custom-card d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon sx={{ fontSize: 22, color }} />
      </div>
      <div>
        <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: '1.75rem', fontWeight: 800, color: '#111827', lineHeight: 1 }}>{value ?? 0}</div>
      </div>
    </div>
  );
}

function ModalConfirmarEntrega({ open, onClose, pedido, onConfirm }) {
  const [foto, setFoto] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleConfirm = async () => {
    setEnviando(true);
    await onConfirm(pedido, foto);
    setEnviando(false);
    setFoto('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '0.9rem' }}>✓ Confirmar entrega</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 12 }}>
          Pedido <strong>{pedido?.numero}</strong> — {pedido?.clientNom}
        </p>
        <TextField
          label="Observaciones (opcional)"
          multiline rows={3} fullWidth size="small"
          value={foto} onChange={e => setFoto(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" size="small">Cancelar</Button>
        <Button onClick={handleConfirm} disabled={enviando} variant="contained" size="small"
          sx={{ background: '#215732', '&:hover': { background: '#154a19' } }}>
          {enviando ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ModalProrrogar({ open, onClose, pedido, onConfirm }) {
  const [fecha, setFecha] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleConfirm = async () => {
    if (!fecha) return;
    setEnviando(true);
    await onConfirm(pedido, fecha);
    setEnviando(false);
    setFecha('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '0.9rem' }}>📅 Aplazar entrega</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 12 }}>
          Pedido <strong>{pedido?.numero}</strong>
        </p>
        <TextField
          label="Nueva fecha de entrega" type="date" fullWidth size="small"
          InputLabelProps={{ shrink: true }}
          value={fecha} onChange={e => setFecha(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" size="small">Cancelar</Button>
        <Button onClick={handleConfirm} disabled={enviando || !fecha} variant="contained" size="small"
          sx={{ background: '#215732', '&:hover': { background: '#154a19' } }}>
          {enviando ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Aplazar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ModalRechazar({ open, onClose, pedido, onConfirm }) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleConfirm = async () => {
    if (!motivo.trim()) return;
    setEnviando(true);
    await onConfirm(pedido, motivo);
    setEnviando(false);
    setMotivo('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#C62828' }}>✕ Cliente rechaza entrega</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 12 }}>
          Pedido <strong>{pedido?.numero}</strong> — {pedido?.clientNom}
        </p>
        <TextField
          label="Motivo del rechazo *" multiline rows={3} fullWidth size="small"
          value={motivo} onChange={e => setMotivo(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" size="small">Cancelar</Button>
        <Button onClick={handleConfirm} disabled={enviando || !motivo.trim()} variant="contained" size="small"
          sx={{ background: '#C62828', '&:hover': { background: '#991b1b' } }}>
          {enviando ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Confirmar rechazo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ModalDetalle({ open, onClose, pedido }) {
  if (!pedido) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Datos del pedido</DialogTitle>
      <DialogContent>
        <div className="row g-3 mt-1">
          {[
            ['Número', pedido.numero],
            ['Cliente', pedido.clientNom],
            ['Teléfono', pedido.telefon],
            ['Fecha reparto', pedido.dataReparto],
            ['Ruta', pedido.ruta],
            ['Estado', <EstadoBadge key="e" estado={pedido.estat} />],
          ].map(([label, val]) => (
            <div key={label} className="col-6">
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{val || '—'}</div>
            </div>
          ))}

          <div className="col-12">
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>
              {pedido.piezas?.length > 1 ? `Piezas (${pedido.piezas.length})` : 'Pieza'}
            </div>
            {(pedido.piezas?.length ? pedido.piezas : [{ id: 'unica', peca: pedido.peca, refPeca: pedido.refPeca }]).map((pieza) => (
              <div key={pieza.id} style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: 4 }}>
                {pieza.peca || '—'}
                {pieza.refPeca && <span style={{ color: '#215732', fontWeight: 700 }}> — {pieza.refPeca}</span>}
              </div>
            ))}
          </div>

          <div className="col-12">
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>Dirección</div>
            <div className="d-flex align-items-start gap-2">
              <div style={{ fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'pre-line', flex: 1 }}>
                {pedido.adreca || '—'}
              </div>
              {pedido.adreca && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pedido.adreca)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    background: '#215732',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 12px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  📍 Google Maps
                </a>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" size="small">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function AccionesMenu({ pedido, onAction }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = React.useRef(null);
  const enReparto = pedido.estat === 'En reparto';
  const pendienteReparto = pedido.estat === 'Pendiente reparto';
  const esRechazado = pedido.estat === 'Rechazado';

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.right - 200,
      });
    }
    setOpen(v => !v);
  };

  return (
    <div style={{ display: 'inline-block' }}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: 4,
          color: '#6b7280',
          fontSize: '1rem',
          lineHeight: 1,
        }}
      >
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
          <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#6b7280', display: 'block' }} />
          <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#6b7280', display: 'block' }} />
          <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#6b7280', display: 'block' }} />
        </span>
      </button>

      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            zIndex: 1000,
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 200, overflow: 'hidden',
          }}>
            <button className="dropdown-item py-2 px-3" style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={() => { onAction('detail', pedido); setOpen(false); }}>
              <FontAwesomeIcon icon={faEye} style={{ color: '#215732' }} /> Datos completos
            </button>
            {enReparto && <>
              <button className="dropdown-item py-2 px-3" style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 8 }}
                onClick={() => { onAction('confirmar', pedido); setOpen(false); }}>
                <FontAwesomeIcon icon={faCheck} style={{ color: '#2E7D32' }} /> Confirmar entrega
              </button>
              <button className="dropdown-item py-2 px-3" style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 8 }}
                onClick={() => { onAction('aplazar', pedido); setOpen(false); }}>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#1565C0' }} /> Aplazar entrega
              </button>
              <div style={{ height: 1, background: '#f3f4f6', margin: '2px 0' }} />
              <button className="dropdown-item py-2 px-3" style={{ fontSize: '0.8rem', color: '#C62828', display: 'flex', alignItems: 'center', gap: 8 }}
                onClick={() => { onAction('rechazar', pedido); setOpen(false); }}>
                <FontAwesomeIcon icon={faTimes} style={{ color: '#C62828' }} /> Cliente rechaza
              </button>
            </>}
            {pendienteReparto && (
              <button className="dropdown-item py-2 px-3" style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 8 }}
                onClick={() => { onAction('aplazar', pedido); setOpen(false); }}>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#1565C0' }} /> Aplazar entrega
              </button>
            )}
            {esRechazado && (
              <button className="dropdown-item py-2 px-3" style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 8 }}
                onClick={() => { onAction('ver-rechazo', pedido); setOpen(false); }}>
                <FontAwesomeIcon icon={faEye} style={{ color: '#C62828' }} /> Ver motivo rechazo
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Agrupa las filas (1 por pieza) en 1 tarjeta por pedido ──────────────────
const groupByPedido = (filas) => {
  const porPedido = new Map();
  filas.forEach((row) => {
    const key = row.pedidoId ?? row.id;
    if (!porPedido.has(key)) {
      porPedido.set(key, { ...row, piezas: [] });
    }
    porPedido.get(key).piezas.push({ id: row.id, peca: row.peca, refPeca: row.refPeca });
  });
  return Array.from(porPedido.values());
};

function TablaPedidos({ filas = [], onAction, mostrarComprovant = false }) {
  if (filas.length === 0) {
    return (
      <div className="text-center py-5" style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
        No hay pedidos para mostrar
      </div>
    );
  }

  const pedidos = groupByPedido(filas);

  return (
    <div className="table-responsive">
      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Fecha reparto</th>
            <th>Estado</th>
            <th>Cliente</th>
            <th>Pieza(s)</th>
            {mostrarComprovant && <th className="text-center">Comprovante</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {pedidos.map((pedido) => (
            <tr key={pedido.pedidoId ?? pedido.id}>
              <td>
                <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#215732' }}>{pedido.numero}</div>
                {pedido.ruta && <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>🛣 {pedido.ruta}</div>}
              </td>
              <td style={{ fontSize: '0.8rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{pedido.dataReparto}</td>
              <td><EstadoBadge estado={pedido.estat} /></td>
              <td>
                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{pedido.clientNom}</div>
                {pedido.adreca && <div style={{ fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'pre-line' }}>{pedido.adreca}</div>}
                {pedido.telefon && <a href={`tel:${pedido.telefon}`} style={{ fontSize: '0.75rem', color: '#215732', fontWeight: 600 }}>{pedido.telefon}</a>}
              </td>
              <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                {pedido.piezas.map((pieza) => (
                  <div key={pieza.id} style={{ marginBottom: 6 }}>
                    {pieza.peca}
                    {pieza.refPeca && <div style={{ color: '#215732', fontWeight: 700 }}>{pieza.refPeca}</div>}
                  </div>
                ))}
              </td>
              {mostrarComprovant && (
                <td className="text-center">
                  {pedido.comprovantText && (
                    <button onClick={() => onAction('comprovant', pedido)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#215732' }}>
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                  )}
                </td>
              )}
              <td><AccionesMenu pedido={pedido} onAction={onAction} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GestionRutas() {
  const PAGE_OPTIONS = [10, 25, 50, 100];

  const [clientes, setClientes] = useState([]);
  const [cargandoClientes, setCargandoClientes] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroRuta, setFiltroRuta] = useState('');
  const [orden, setOrden] = useState({ campo: 'nombre', dir: 'asc' });
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(10);

  const [modalAddOpen, setModalAddOpen] = useState(false);
  const [busquedaAdd, setBusquedaAdd] = useState('');
  const [clientesSinRuta, setClientesSinRuta] = useState([]);
  const [cargandoSinRuta, setCargandoSinRuta] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [rutaAsignar, setRutaAsignar] = useState('');
  const [guardando, setGuardando] = useState(false);

  const [modalEditOpen, setModalEditOpen] = useState(false);
  const [clienteEdit, setClienteEdit] = useState(null);
  const [rutaEdit, setRutaEdit] = useState('');

  const { rutas } = useRutas();

  const [modalComentarisOpen, setModalComentarisOpen] = useState(false);
  const [clienteComentaris, setClienteComentaris] = useState(null);
  const [contactos, setContactos] = useState([]);
  const [cargandoContactos, setCargandoContactos] = useState(false);
  const [nuevoContacto, setNuevoContacto] = useState({ fecha: new Date().toISOString().split('T')[0], observaciones: '', volver_contactar: true });
  const [guardandoContacto, setGuardandoContacto] = useState(false);

  const cargarContactos = useCallback(async (detalleId) => {
    if (!detalleId) return;
    setCargandoContactos(true);
    try {
      const res = await API.get(`/detalle-comercial/${detalleId}`);
      setContactos(res.data?.data?.contactos ?? []);
    } catch (e) {
      console.error('Error carregant contactes:', e);
    } finally {
      setCargandoContactos(false);
    }
  }, []);

  const handleAbrirComentaris = (cliente) => {
    setClienteComentaris(cliente);
    setModalComentarisOpen(true);
    cargarContactos(cliente.detalle_id);
  };

  const handleGuardarContacto = async () => {
    if (!clienteComentaris?.detalle_id || !nuevoContacto.observaciones.trim()) return;
    setGuardandoContacto(true);
    try {
      const res = await API.post(`/detalle-comercial/${clienteComentaris.detalle_id}/contacto`, {
        fecha: nuevoContacto.fecha,
        observaciones: nuevoContacto.observaciones,
        volver_contactar: nuevoContacto.volver_contactar,
      });
      setContactos(prev => [res.data?.data, ...prev]);
      setNuevoContacto({ fecha: new Date().toISOString().split('T')[0], observaciones: '', volver_contactar: true });
      toast.success('Contacto registrado!');
      await cargarClientes();
    } catch (e) {
      toast.error('Error guardando contacto');
    } finally {
      setGuardandoContacto(false);
    }
  };

  const cargarClientes = useCallback(async () => {
    setCargandoClientes(true);
    try {
      const res = await API.get('/entities-ruta');
      setClientes(res.data?.data ?? []);
    } catch (e) {
      console.error('Error cargando clientes con ruta:', e);
    } finally {
      setCargandoClientes(false);
    }
  }, []);

  useEffect(() => { cargarClientes(); }, [cargarClientes]);

  useEffect(() => {
    if (!modalAddOpen || !busquedaAdd.trim()) {
      setClientesSinRuta([]);
      return;
    }
    setCargandoSinRuta(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await API.post('/entities/buscar', { search: busquedaAdd.trim() });
        const tots = res.data?.data ?? [];
        setClientesSinRuta(tots.filter(c => !c.commercial_delivery));
      } catch (e) {
        console.error(e);
      } finally {
        setCargandoSinRuta(false);
      }
    }, 2000);
    return () => clearTimeout(timeout);
  }, [busquedaAdd, modalAddOpen]);

  const clientesFiltrats = clientes
    .filter(c => {
      if (filtroRuta && c.commercial_delivery !== filtroRuta) return false;
      if (!busqueda) return true;
      const q = busqueda.toLowerCase();
      return (
        c.nombre?.toLowerCase().includes(q) ||
        c.telefono?.includes(q) ||
        c.cif?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const va = (a[orden.campo] || '').toLowerCase();
      const vb = (b[orden.campo] || '').toLowerCase();
      return orden.dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

  const total = clientesFiltrats.length;
  const totalPagines = Math.ceil(total / porPagina);
  const clientesPagina = clientesFiltrats.slice((pagina - 1) * porPagina, pagina * porPagina);

  const toggleOrden = (campo) => {
    setOrden(prev => prev.campo === campo
      ? { campo, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { campo, dir: 'asc' }
    );
    setPagina(1);
  };

  const SortIcon = ({ campo }) => {
    if (orden.campo !== campo) return <span style={{ color: '#d1d5db', marginLeft: 4 }}>↕</span>;
    return <span style={{ color: '#215732', marginLeft: 4 }}>{orden.dir === 'asc' ? '↑' : '↓'}</span>;
  };

  const handleAsignarRuta = async () => {
    if (!clienteSeleccionado) return;
    setGuardando(true);
    try {
      await API.post(`/entities/${clienteSeleccionado.id}/update`, {
        ...clienteSeleccionado,
        commercial_delivery: rutaAsignar,
      });
      toast.success(`Ruta ${rutaAsignar} asignada a ${clienteSeleccionado.nombre}`);
      setModalAddOpen(false);
      setClienteSeleccionado(null);
      setBusquedaAdd('');
      await cargarClientes();
    } catch (e) {
      toast.error('Error asignando ruta');
    } finally {
      setGuardando(false);
    }
  };

  const handleEditarRuta = async () => {
    if (!clienteEdit) return;
    setGuardando(true);
    try {
      await API.post(`/entities/${clienteEdit.id}/update`, {
        nombre: clienteEdit.nombre,
        telefono: clienteEdit.telefono || '',
        cif: clienteEdit.cif || '',
        b2b: clienteEdit.b2b ?? true,
        tipo_cliente_id: clienteEdit.tipo_cliente_id ?? 2,
        cuenta_grande: clienteEdit.cuenta_grande ?? false,
        bloqueado: clienteEdit.bloqueado ?? false,
        sepa: clienteEdit.sepa ?? false,
        mostrar_referencia: clienteEdit.mostrar_referencia ?? false,
        aviso_incidencias: clienteEdit.aviso_incidencias ?? true,
        commercial_delivery: rutaEdit || null,
      });
      toast.success('Ruta actualizada correctamente');
      setModalEditOpen(false);
      setClienteEdit(null);
      await cargarClientes();
    } catch (e) {
      toast.error('Error actualizando ruta');
      console.error(e);
    } finally {
      setGuardando(false);
    }
  };

  const INPUT_STYLE = { height: 36, border: '1px solid #ced4da', borderRadius: 6, padding: '6px 10px', fontSize: '0.875rem', background: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box' };
  const RUTA_PALETTE = [
    { bg: '#E3F2FD', color: '#1565C0' },
    { bg: '#FFF3E0', color: '#E65100' },
    { bg: '#F3E5F5', color: '#7B1FA2' },
    { bg: '#E8F5E9', color: '#2E7D32' },
    { bg: '#FCE4EC', color: '#C2185B' },
  ];
  const badgeDeRuta = (codigo) => {
    const idx = rutas.findIndex(r => r.codigo === codigo);
    return idx === -1 ? { bg: '#f3f4f6', color: '#374151' } : RUTA_PALETTE[idx % RUTA_PALETTE.length];
  };
  const nombreDeRuta = (codigo) => rutas.find(r => r.codigo === codigo)?.nombre ?? codigo;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>Buscar</div>
          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ced4da', borderRadius: 6, background: '#fff', height: 36, padding: '0 10px', gap: 6 }}>
            <SearchIcon sx={{ fontSize: 16, color: '#9ca3af', flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Nombre, teléfono o CIF..."
              value={busqueda}
              onChange={e => { setBusqueda(e.target.value); setPagina(1); }}
              style={{ border: 'none', outline: 'none', fontSize: '0.875rem', background: 'transparent', width: '100%' }}
            />
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>Por página</div>
          <select
            style={{ ...INPUT_STYLE, width: 90, cursor: 'pointer' }}
            value={porPagina}
            onChange={e => { setPorPagina(Number(e.target.value)); setPagina(1); }}
          >
            {PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>Ruta</div>
          <select
            style={{ height: 36, border: '1px solid #ced4da', borderRadius: 6, padding: '6px 10px', fontSize: '0.875rem', background: '#fff', cursor: 'pointer', outline: 'none', width: 140 }}
            value={filtroRuta}
            onChange={e => { setFiltroRuta(e.target.value); setPagina(1); }}
          >
            <option value="">Todas</option>
            {rutas.map(r => <option key={r.codigo} value={r.codigo}>{r.nombre}</option>)}
          </select>
        </div>
        <button
          className="btn btn-add"
          style={{ height: 36 }}
          onClick={() => { setModalAddOpen(true); setClienteSeleccionado(null); setBusquedaAdd(''); }}
        >
          <span className="add"><PersonAddIcon sx={{ fontSize: 16 }} /></span>
          Añadir cliente
        </button>
      </div>

      <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: 10 }}>
        {total} cliente{total !== 1 ? 's' : ''} en rutas
        {busqueda && ` · filtrado por "${busqueda}"`}
      </div>

      {cargandoClientes
        ? <div className="text-center py-5"><CircularProgress sx={{ color: '#215732' }} /></div>
        : (
          <>
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleOrden('nombre')}>
                      Nombre <SortIcon campo="nombre" />
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleOrden('telefono')}>
                      Teléfono <SortIcon campo="telefono" />
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleOrden('cif')}>
                      CIF <SortIcon campo="cif" />
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleOrden('commercial_delivery')}>
                      Ruta <SortIcon campo="commercial_delivery" />
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleOrden('ultimo_contacto')}>
                      Última visita <SortIcon campo="ultimo_contacto" />
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {clientesPagina.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-4" style={{ color: '#9ca3af' }}>
                      {busqueda ? `No hay resultados para "${busqueda}"` : 'No hay clientes con ruta asignada'}
                    </td></tr>
                  )}
                  {clientesPagina.map(c => {
                    const badge = badgeDeRuta(c.commercial_delivery);
                    return (
                      <tr key={c.id}>
                        <td style={{ fontWeight: 600 }}>{c.nombre}</td>
                        <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>{c.telefono || '—'}</td>
                        <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>{c.cif || '—'}</td>
                        <td>
                          <span style={{ background: badge.bg, color: badge.color, fontSize: '0.75rem', fontWeight: 700, padding: '3px 10px', borderRadius: 99 }}>
                            {nombreDeRuta(c.commercial_delivery)}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.8rem', color: c.ultimo_contacto ? '#215732' : '#9ca3af', whiteSpace: 'nowrap' }}>
                          {c.ultimo_contacto
                            ? new Date(c.ultimo_contacto).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : '—'
                          }
                        </td>
                        <td className="text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            <button
                              onClick={() => handleAbrirComentaris(c)}
                              style={{ background: 'none', border: '1px solid #6b7280', color: '#6b7280', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 }}
                              title="Ver comentarios"
                            >
                              💬
                            </button>
                            <button
                              onClick={() => { setClienteEdit(c); setRutaEdit(c.commercial_delivery); setModalEditOpen(true); }}
                              style={{ background: 'none', border: `1px solid #215732`, color: '#215732', borderRadius: 6, padding: '4px 12px', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 }}
                            >
                              Editar ruta
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPagines > 1 && (
              <div className="d-flex align-items-center justify-content-between mt-3">
                <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                  Página {pagina} de {totalPagines} · {total} resultados
                </span>
                <div className="d-flex gap-1">
                  <button
                    onClick={() => setPagina(1)} disabled={pagina === 1}
                    style={{ padding: '4px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: pagina === 1 ? 'default' : 'pointer', color: pagina === 1 ? '#d1d5db' : '#374151', fontSize: '0.8rem' }}
                  >«</button>
                  <button
                    onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
                    style={{ padding: '4px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: pagina === 1 ? 'default' : 'pointer', color: pagina === 1 ? '#d1d5db' : '#374151', fontSize: '0.8rem' }}
                  >‹</button>
                  {Array.from({ length: Math.min(5, totalPagines) }, (_, i) => {
                    const p = Math.min(Math.max(pagina - 2, 1) + i, totalPagines);
                    return (
                      <button key={p} onClick={() => setPagina(p)}
                        style={{ padding: '4px 10px', border: `1px solid ${p === pagina ? '#215732' : '#e5e7eb'}`, borderRadius: 6, background: p === pagina ? '#215732' : '#fff', color: p === pagina ? '#fff' : '#374151', cursor: 'pointer', fontSize: '0.8rem', fontWeight: p === pagina ? 700 : 400 }}>
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPagina(p => Math.min(totalPagines, p + 1))} disabled={pagina === totalPagines}
                    style={{ padding: '4px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: pagina === totalPagines ? 'default' : 'pointer', color: pagina === totalPagines ? '#d1d5db' : '#374151', fontSize: '0.8rem' }}
                  >›</button>
                  <button
                    onClick={() => setPagina(totalPagines)} disabled={pagina === totalPagines}
                    style={{ padding: '4px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: pagina === totalPagines ? 'default' : 'pointer', color: pagina === totalPagines ? '#d1d5db' : '#374151', fontSize: '0.8rem' }}
                  >»</button>
                </div>
              </div>
            )}
          </>
        )
      }

      <Dialog open={modalAddOpen} onClose={() => setModalAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '0.9rem', borderBottom: '1px solid #f3f4f6', pb: 1.5 }}>
          Añadir cliente a ruta
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <div className="d-flex gap-2 mb-3">
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ced4da', borderRadius: 6, background: '#fff', height: 36, padding: '0 10px', gap: 6, flex: 1 }}>
              <SearchIcon sx={{ fontSize: 16, color: '#9ca3af', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Buscar por nombre, teléfono o CIF..."
                value={busquedaAdd}
                onChange={e => setBusquedaAdd(e.target.value)}
                style={{ border: 'none', outline: 'none', fontSize: '0.875rem', background: 'transparent', width: '100%' }}
                autoFocus
              />
            </div>
            <select
              value={rutaAsignar}
              onChange={e => setRutaAsignar(e.target.value)}
              style={{ ...INPUT_STYLE, width: 140, cursor: 'pointer' }}
            >
              <option value="">Selecciona ruta...</option>
              {rutas.map(r => <option key={r.codigo} value={r.codigo}>{r.nombre}</option>)}
            </select>
          </div>

          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #f3f4f6', borderRadius: 8 }}>
            {cargandoSinRuta
              ? <div className="text-center py-4"><CircularProgress size={24} sx={{ color: '#215732' }} /></div>
              : clientesSinRuta.length === 0
                ? <div className="text-center py-4" style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                  {busquedaAdd ? 'Sin resultados' : 'Escribe para buscar clientes'}
                </div>
                : clientesSinRuta.map(c => (
                  <div
                    key={c.id}
                    onClick={() => setClienteSeleccionado(prev => prev?.id === c.id ? null : c)}
                    style={{
                      padding: '10px 14px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f9fafb',
                      background: clienteSeleccionado?.id === c.id ? '#F1F8E9' : '#fff',
                      borderLeft: clienteSeleccionado?.id === c.id ? '3px solid #215732' : '3px solid transparent',
                      transition: 'all .1s',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{c.nombre}</div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{c.cif} · {c.telefono}</div>
                  </div>
                ))
            }
          </div>

          {clienteSeleccionado && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#F1F8E9', borderRadius: 8, border: '1px solid #A5D6A7', fontSize: '0.875rem' }}>
              <span style={{ fontWeight: 700, color: '#215732' }}>✓ Seleccionado:</span> {clienteSeleccionado.nombre}
              {rutaAsignar && <>{' → '}<span style={{ fontWeight: 700, color: badgeDeRuta(rutaAsignar).color }}>{nombreDeRuta(rutaAsignar)}</span></>}
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #f3f4f6', px: 2.5, py: 1.5 }}>
          <Button onClick={() => setModalAddOpen(false)} color="inherit" size="small">Cancelar</Button>
          <Button
            onClick={handleAsignarRuta}
            disabled={!clienteSeleccionado || !rutaAsignar || guardando}
            variant="contained" size="small"
            sx={{ background: '#215732', '&:hover': { background: '#154a19' } }}
          >
            {guardando ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Asignar ruta'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalComentarisOpen} onClose={() => { setModalComentarisOpen(false); setContactos([]); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '0.9rem', borderBottom: '1px solid #f3f4f6', pb: 1.5 }}>
          💬 Comentarios comerciales — {clienteComentaris?.nombre}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {clienteComentaris?.ultimas_observaciones && (
            <div style={{ background: '#F1F8E9', border: '1px solid #A5D6A7', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.875rem' }}>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>Últimas observaciones</div>
              <div style={{ color: '#374151' }}>{clienteComentaris.ultimas_observaciones}</div>
            </div>
          )}

          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 8 }}>
            Historial de contactos
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 20, border: '1px solid #f3f4f6', borderRadius: 8 }}>
            {cargandoContactos
              ? <div className="text-center py-3"><CircularProgress size={20} sx={{ color: '#215732' }} /></div>
              : contactos.length === 0
                ? <div className="text-center py-4" style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Sin contactos registrados</div>
                : [...contactos].sort((a, b) => new Date(b.fecha) - new Date(a.fecha)).map((c, i) => (
                  <div key={c.id ?? i} style={{ padding: '10px 14px', borderBottom: '1px solid #f9fafb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#215732' }}>
                        {c.fecha ? new Date(c.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                      </span>
                      {c.owner?.username && (
                        <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{c.owner.username}</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#374151' }}>{c.observaciones || '—'}</div>
                  </div>
                ))
            }
          </div>

          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 8 }}>
            Añadir nuevo contacto
          </div>
          <div className="d-flex flex-column gap-2">
            <input
              type="date"
              className="form-control form-control-sm"
              value={nuevoContacto.fecha}
              onChange={e => setNuevoContacto(p => ({ ...p, fecha: e.target.value }))}
              style={{ width: 160 }}
            />
            <TextField
              multiline rows={3} fullWidth size="small"
              placeholder="Observaciones del contacto..."
              value={nuevoContacto.observaciones}
              onChange={e => setNuevoContacto(p => ({ ...p, observaciones: e.target.value }))}
            />
            <div className="d-flex align-items-center gap-2">
              <input
                type="checkbox"
                id="volver_contactar"
                checked={nuevoContacto.volver_contactar}
                onChange={e => setNuevoContacto(p => ({ ...p, volver_contactar: e.target.checked }))}
              />
              <label htmlFor="volver_contactar" style={{ fontSize: '0.875rem', cursor: 'pointer', marginBottom: 0 }}>
                Volver a contactar
              </label>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #f3f4f6', px: 2.5, py: 1.5 }}>
          <Button onClick={() => { setModalComentarisOpen(false); setContactos([]); }} color="inherit" size="small">Cerrar</Button>
          <Button
            onClick={handleGuardarContacto}
            disabled={guardandoContacto || !nuevoContacto.observaciones.trim()}
            variant="contained" size="small"
            sx={{ background: '#215732', '&:hover': { background: '#154a19' } }}
          >
            {guardandoContacto ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Guardar contacto'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalEditOpen} onClose={() => setModalEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Editar ruta</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 16 }}>
            <strong>{clienteEdit?.nombre}</strong>
          </p>
          <div>
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 6 }}>Ruta asignada</div>
            <select
              value={rutaEdit}
              onChange={e => setRutaEdit(e.target.value)}
              style={{ ...INPUT_STYLE, cursor: 'pointer' }}
            >
              {rutas.map(r => <option key={r.codigo} value={r.codigo}>{r.nombre}</option>)}
              <option value="">Desactivar (quitar ruta)</option>
            </select>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalEditOpen(false)} color="inherit" size="small">Cancelar</Button>
          <Button
            onClick={handleEditarRuta}
            disabled={guardando}
            variant="contained" size="small"
            sx={{ background: '#215732', '&:hover': { background: '#154a19' } }}
          >
            {guardando ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function DashboardKpis() {
  const { rutas } = useRutas();
  const hoyKpis = new Date().toISOString().split('T')[0];
  const haceUnaSemana = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [filtros, setFiltros] = useState({ ruta: '', desde: haceUnaSemana, hasta: hoyKpis });
  const [kpis, setKpis] = useState(null);
  const [resumen, setResumen] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async (params = filtros) => {
    setCargando(true);
    setError(null);
    try {
      const query = new URLSearchParams();
      if (params.ruta) query.append('ruta', params.ruta);
      if (params.desde) query.append('desde', params.desde);
      if (params.hasta) query.append('hasta', params.hasta);
      const [kr, sr] = await Promise.all([
        API.get(`/repartos/kpis?${query}`),
        API.get(`/repartos/kpis/resumen-semanal?${query}`),
      ]);
      setKpis(kr.data?.data ?? null);
      setResumen(sr.data?.data ?? []);
    } catch { setError('Error cargando indicadores.'); }
    finally { setCargando(false); }
  }, []);

  const desdeKpis = filtros.desde;
  const hastaKpis = filtros.hasta;
  const rutaKpis = filtros.ruta;
  useEffect(() => { cargar(filtros); }, [desdeKpis, hastaKpis, rutaKpis]);

  const esDefault = !filtros.ruta && filtros.desde === haceUnaSemana && filtros.hasta === hoyKpis;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-end gap-2 mb-4">
        <div>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>Ruta</div>
          <select
            style={{ width: 160, height: 38, border: '1px solid #ced4da', borderRadius: 6, padding: '6px 10px', fontSize: '0.875rem', background: '#fff', cursor: 'pointer', outline: 'none' }}
            value={filtros.ruta}
            onChange={e => setFiltros(p => ({ ...p, ruta: e.target.value }))}>
            <option value="">Todas las rutas</option>
            {rutas.map(r => <option key={r.codigo} value={r.codigo}>{r.nombre}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>Desde</div>
          <input type="date" className="form-control" style={{ width: 160, height: 38 }}
            value={filtros.desde} max={filtros.hasta || hoyKpis}
            onChange={e => setFiltros(p => ({ ...p, desde: e.target.value }))} />
        </div>
        <div style={{ paddingBottom: 2, color: '#9ca3af', lineHeight: '38px', marginTop: 18 }}>—</div>
        <div>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>Hasta</div>
          <input type="date" className="form-control" style={{ width: 160, height: 38 }}
            value={filtros.hasta} max={hoyKpis} min={filtros.desde}
            onChange={e => setFiltros(p => ({ ...p, hasta: e.target.value }))} />
        </div>
        <button
          style={{
            height: 38, background: esDefault ? '#e5e7eb' : '#215732',
            color: esDefault ? '#9ca3af' : '#fff',
            border: 'none', borderRadius: 6, padding: '0 14px',
            fontSize: '0.875rem', cursor: esDefault ? 'default' : 'pointer', fontWeight: 600,
          }}
          disabled={esDefault}
          onClick={() => { setFiltros({ ruta: '', desde: haceUnaSemana, hasta: hoyKpis }); cargar({ ruta: '', desde: haceUnaSemana, hasta: hoyKpis }); }}>
          Limpiar filtros
        </button>
      </div>

      {error && <div className="alert alert-danger">{error} <button className="btn btn-sm btn-link" onClick={() => cargar(filtros)}>Reintentar</button></div>}
      {cargando && <div className="text-center py-5"><CircularProgress sx={{ color: '#215732' }} /></div>}

      {kpis && (
        <>
          <div className="row g-3 mb-4">
            {[
              { label: 'Repartos esta semana', value: kpis.semana?.total, color: '#215732', bg: '#DAF9E4' },
              { label: 'Repartos este mes', value: kpis.mes?.total, color: '#1565C0', bg: '#E3F2FD' },
              { label: 'Entregados hoy', value: kpis.entregadosHoy?.total, color: '#2E7D32', bg: '#E8F5E9' },
              { label: '% Reparto propio', value: `${kpis.propioVsExterno?.propio ?? 0}%`, color: '#E65100', bg: '#FFF3E0' },
            ].map(({ label, value, color, bg }) => (
              <div key={label} className="col-12 col-sm-6 col-md-3">
                <div className="custom-card text-center">
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: '2rem', fontWeight: 800, color }}>{value ?? 0}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="custom-card">
            <h6 style={{ fontWeight: 700, color: '#215732', marginBottom: 16 }}>Resumen semanal</h6>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Día</th>
                    {rutas.map(r => <th key={r.codigo}>{r.nombre}</th>)}
                    <th>Total</th><th>% Propio</th><th>Incidencias</th>
                  </tr>
                </thead>
                <tbody>
                  {resumen.length === 0 && <tr><td colSpan={rutas.length + 4} className="text-center py-3" style={{ color: '#9ca3af' }}>Sin datos</td></tr>}
                  {resumen.filter(r => {
                    if (filtros.desde && r.fecha && r.fecha < filtros.desde) return false;
                    if (filtros.hasta && r.fecha && r.fecha > filtros.hasta) return false;
                    return true;
                  }).map(r => (
                    <tr key={r.dia} style={{ background: r.esHoy ? '#F1F8E9' : 'transparent', fontWeight: r.esHoy ? 600 : 400 }}>
                      <td>{r.dia}{r.esHoy && <span className="badge ms-2" style={{ background: '#215732', color: '#fff', fontSize: '0.65rem' }}>hoy</span>}</td>
                      {rutas.map(ruta => (
                        <td key={ruta.codigo} style={{ color: '#215732' }}>{r.porRuta?.[ruta.codigo] ?? '—'}</td>
                      ))}
                      <td style={{ fontWeight: 700 }}>{r.total ?? '—'}</td>
                      <td>{r.propio != null ? `${r.propio}%` : '—'}</td>
                      <td style={{ color: r.incidencias > 0 ? '#C62828' : '#2E7D32', fontWeight: r.incidencias > 0 ? 700 : 400 }}>
                        {r.incidencias != null ? (r.incidencias > 0 ? `⚠ ${r.incidencias}` : '✓ 0') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Paginacion({ pagina, totalPaginas, total, onChange }) {
  if (totalPaginas <= 1) return null;
  return (
    <div className="d-flex align-items-center justify-content-between mt-3">
      <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
        Página {pagina} de {totalPaginas} · {total} resultados
      </span>
      <div className="d-flex gap-1">
        <button onClick={() => onChange(1)} disabled={pagina === 1}
          style={{ padding: '4px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: pagina === 1 ? 'default' : 'pointer', color: pagina === 1 ? '#d1d5db' : '#374151', fontSize: '0.8rem' }}>«</button>
        <button onClick={() => onChange(Math.max(1, pagina - 1))} disabled={pagina === 1}
          style={{ padding: '4px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: pagina === 1 ? 'default' : 'pointer', color: pagina === 1 ? '#d1d5db' : '#374151', fontSize: '0.8rem' }}>‹</button>
        {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
          const p = Math.min(Math.max(pagina - 2, 1) + i, totalPaginas);
          return (
            <button key={p} onClick={() => onChange(p)}
              style={{ padding: '4px 10px', border: `1px solid ${p === pagina ? '#215732' : '#e5e7eb'}`, borderRadius: 6, background: p === pagina ? '#215732' : '#fff', color: p === pagina ? '#fff' : '#374151', cursor: 'pointer', fontSize: '0.8rem', fontWeight: p === pagina ? 700 : 400 }}>
              {p}
            </button>
          );
        })}
        <button onClick={() => onChange(Math.min(totalPaginas, pagina + 1))} disabled={pagina === totalPaginas}
          style={{ padding: '4px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: pagina === totalPaginas ? 'default' : 'pointer', color: pagina === totalPaginas ? '#d1d5db' : '#374151', fontSize: '0.8rem' }}>›</button>
        <button onClick={() => onChange(totalPaginas)} disabled={pagina === totalPaginas}
          style={{ padding: '4px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: pagina === totalPaginas ? 'default' : 'pointer', color: pagina === totalPaginas ? '#d1d5db' : '#374151', fontSize: '0.8rem' }}>»</button>
      </div>
    </div>
  );
}

function SelectorPorPagina({ valor, onChange }) {
  return (
    <select
      style={{ height: 36, border: '1px solid #ced4da', borderRadius: 6, padding: '6px 10px', fontSize: '0.875rem', background: '#fff', cursor: 'pointer', outline: 'none', width: 90 }}
      value={valor}
      onChange={e => onChange(Number(e.target.value))}
    >
      {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

const MOCK_KPIS = { en_reparto: 0, entregados_hoy: 0, preparados_manana: 0, aplazados: 0 };
const TAB_VACIA = { data: [], pagination: { page: 1, limit: 25, total: 0, totalPages: 1 }, status: 'idle' };

export default function Reparto() {
  const dispatch = useDispatch();
  const location = useLocation();
  const isDevice = new URLSearchParams(location.search).get('focus') === 'true';
  const { rutas } = useRutas();
  const repartos = useSelector(state => state.repartos);
  const [tab, setTab] = useState('dashboard');
  const [tabHistorico, setTabHistorico] = useState('entregados');
  const [modal, setModal] = useState({ tipo: null, pedido: null });

  const [filtros, setFiltros] = useState({ fecha: '', ruta: '', estado: '', cerca: '' });

  const [paginaGeneral, setPaginaGeneral] = useState(1);
  const [porPaginaGeneral, setPorPaginaGeneral] = useState(25);
  const [paginaHistorico, setPaginaHistorico] = useState(1);
  const [porPaginaHistorico, setPorPaginaHistorico] = useState(25);

  useEffect(() => {
    setPaginaGeneral(1);
    setPaginaHistorico(1);
  }, [filtros.fecha, filtros.ruta]);

  useEffect(() => { setPaginaHistorico(1); }, [tabHistorico]);

  const peticionesEnVuelo = useRef(0);
  const empezarCarga = useCallback(() => {
    peticionesEnVuelo.current += 1;
    dispatch({ type: 'loading/set', payload: true });
  }, [dispatch]);
  const terminarCarga = useCallback(() => {
    peticionesEnVuelo.current = Math.max(0, peticionesEnVuelo.current - 1);
    if (peticionesEnVuelo.current === 0) dispatch({ type: 'loading/set', payload: false });
  }, [dispatch]);

  useEffect(() => {
    if (tab === 'dashboard') {
      empezarCarga();
      dispatch(fetchRepartos({
        estado: 'general',
        fecha: filtros.fecha || null,
        ruta: filtros.ruta || null,
        page: paginaGeneral,
        limit: porPaginaGeneral,
      })).finally(terminarCarga);
    } else if (tab === 'historico') {
      empezarCarga();
      dispatch(fetchRepartos({
        estado: tabHistorico,
        fecha: filtros.fecha || null,
        ruta: filtros.ruta || null,
        page: paginaHistorico,
        limit: porPaginaHistorico,
      })).finally(terminarCarga);
    }
  }, [tab, tabHistorico, filtros.fecha, filtros.ruta, paginaGeneral, porPaginaGeneral, paginaHistorico, porPaginaHistorico]);

  const filtrarPedidos = (filas) => {
    return filas.filter(p => {
      if (filtros.estado && p.estat !== filtros.estado) return false;
      if (filtros.cerca) {
        const q = filtros.cerca.toLowerCase().trim();
        const cpMatch = p.adreca?.toLowerCase().includes(q);
        const numMatch = p.numero?.toLowerCase().includes(q);
        if (!cpMatch && !numMatch) return false;
      }
      return true;
    });
  };

  const kpis = repartos?.kpis ?? MOCK_KPIS;
  const generalTab = repartos?.tabs?.general ?? TAB_VACIA;
  const entregadosTab = repartos?.tabs?.entregados ?? TAB_VACIA;
  const rechazadosTab = repartos?.tabs?.rechazados ?? TAB_VACIA;
  const todosTab = repartos?.tabs?.todos ?? TAB_VACIA;

  const general = filtrarPedidos(generalTab.data);
  const entregados = filtrarPedidos(entregadosTab.data);
  const rechazados = filtrarPedidos(rechazadosTab.data);
  const todos = filtrarPedidos(todosTab.data);
  const refreshing = repartos?.refreshing ?? false;

  const handleAccion = (tipo, pedido) => setModal({ tipo, pedido });
  const cerrarModal = () => setModal({ tipo: null, pedido: null });

  const handleConfirmar = async (pedido) => {
    empezarCarga();
    try {
      await dispatch(confirmarEntrega({ id: pedido.pedidoId }));
      toast.success('Entrega confirmada!');
    } finally {
      terminarCarga();
    }
  };

  const handleAplazar = async (pedido, nuevaFecha) => {
    empezarCarga();
    try {
      await dispatch(prorrogarEntrega({ id: pedido.pedidoId, nuevaFecha }));
      toast.success('Entrega aplazada!');
    } finally {
      terminarCarga();
    }
  };

  const handleRechazar = async (pedido, motivo) => {
    empezarCarga();
    try {
      await dispatch(rechazarEntrega({ id: pedido.pedidoId, motivo }));
      toast.success('Rechazo registrado!');
    } finally {
      terminarCarga();
    }
  };

  const handleRefresh = () => {
    empezarCarga();
    if (tab === 'dashboard') {
      dispatch(fetchRefreshRepartos({ estado: 'general', fecha: filtros.fecha || null, ruta: filtros.ruta || null, page: paginaGeneral, limit: porPaginaGeneral })).finally(terminarCarga);
    } else {
      dispatch(fetchRefreshRepartos({ estado: tabHistorico, fecha: filtros.fecha || null, ruta: filtros.ruta || null, page: paginaHistorico, limit: porPaginaHistorico })).finally(terminarCarga);
    }
  };

  return (
    <div
      className={isDevice ? '' : 'content pt-3'}
      style={isDevice ? {
        position: 'fixed',
        inset: 0,
        background: '#fff',
        padding: '16px',
        overflowY: 'auto',
        zIndex: 100,
      } : {}}
    >
      {isDevice && (
        <>
          <ToastContainer position="bottom-center" autoClose={3000} />
          <style>{`
            .MuiDialog-root,
            .MuiPopover-root,
            .MuiModal-root {
              z-index: 10000 !important;
            }
          `}</style>
        </>
      )}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 gap-2">
        <div className="d-flex align-items-center gap-2">
          <LocalShippingIcon sx={{ fontSize: 24, color: '#215732' }} />
          <h4 className="mb-0" style={{ fontWeight: 700, color: '#111827' }}>Reparto Recomotor</h4>
        </div>
        <button className="btn btn-add" onClick={handleRefresh} disabled={refreshing}>
          <span className="add">
            <RefreshIcon sx={{ fontSize: 16, animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </span>
          {refreshing ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-6 col-md-3">
          <KpiCard label="En reparto" value={kpis.en_reparto} icon={LocalShippingIcon} color="#E65100" bg="#FFF3E0" />
        </div>
        <div className="col-12 col-sm-6 col-md-3">
          <KpiCard label="Entregados hoy" value={kpis.entregados_hoy} icon={CheckCircleIcon} color="#2E7D32" bg="#E8F5E9" />
        </div>
        <div className="col-12 col-sm-6 col-md-3">
          <KpiCard label="Preparados mañana" value={kpis.preparados_manana} icon={ScheduleIcon} color="#1565C0" bg="#E3F2FD" />
        </div>
        <div className="col-12 col-sm-6 col-md-3">
          <KpiCard label="Aplazados" value={kpis.aplazados} icon={PauseCircleOutlineIcon} color="#616161" bg="#F5F5F5" />
        </div>
      </div>

      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <TabList onChange={(_, v) => setTab(v)}>
            <Tab value="dashboard" label={
              <span>Dashboard <span className="badge ms-1" style={{ background: '#215732', color: '#fff', fontSize: '0.65rem' }}>{generalTab.pagination.total}</span></span>
            } />
            <Tab value="historico" label="Histórico" />
            <Tab value="rutas" label="Gestión de rutas" />
            <Tab value="kpis" label="Indicadores" />
          </TabList>
        </Box>

        <TabPanel value="dashboard" sx={{ p: 0 }}>
          <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
            <div>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>
                Presupuesto / CP
              </div>
              <input
                type="text" className="form-control" style={{ width: 180 }}
                placeholder="P-2503253 / 25140"
                value={filtros.cerca}
                onChange={e => setFiltros(p => ({ ...p, cerca: e.target.value }))}
              />
            </div>

            <div>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>
                Fecha reparto
              </div>
              <input
                type="date" className="form-control" style={{ width: 170 }}
                value={filtros.fecha}
                onChange={e => setFiltros(p => ({ ...p, fecha: e.target.value }))}
              />
              {filtros.fecha && (
                <button
                  type="button"
                  onClick={() => setFiltros(p => ({ ...p, fecha: '' }))}
                  style={{ background: 'none', border: 'none', color: '#215732', fontSize: '0.72rem', padding: 0, marginTop: 3, cursor: 'pointer' }}
                >
                  Ver todos los días
                </button>
              )}
            </div>

            <div>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>
                Ruta
              </div>
              <select
                style={{ width: 160, border: '1px solid #ced4da', borderRadius: 6, padding: '6px 10px', fontSize: '0.875rem', background: '#fff', cursor: 'pointer', outline: 'none' }}
                value={filtros.ruta}
                onChange={e => setFiltros(p => ({ ...p, ruta: e.target.value }))}
              >
                <option value="">Todas las rutas</option>
                {rutas.map(r => <option key={r.codigo} value={r.codigo}>{r.nombre}</option>)}
              </select>
            </div>

            <div>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>
                Estado
              </div>
              <select
                style={{ width: 200, border: '1px solid #ced4da', borderRadius: 6, padding: '6px 10px', fontSize: '0.875rem', background: '#fff', cursor: 'pointer', outline: 'none' }}
                value={filtros.estado}
                onChange={e => setFiltros(p => ({ ...p, estado: e.target.value }))}
              >
                <option value="">Todos los estados</option>
                <option value="En reparto">En reparto</option>
                <option value="Pendiente reparto">Pendiente reparto</option>
                <option value="Pendiente preparar">Pendiente preparar</option>
              </select>
            </div>

            <div>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>Por página</div>
              <SelectorPorPagina valor={porPaginaGeneral} onChange={n => { setPorPaginaGeneral(n); setPaginaGeneral(1); }} />
            </div>

            <button
              style={{
                alignSelf: 'flex-end',
                background: (!filtros.ruta && !filtros.estado && !filtros.cerca && !filtros.fecha) ? '#e5e7eb' : '#215732',
                color: (!filtros.ruta && !filtros.estado && !filtros.cerca && !filtros.fecha) ? '#9ca3af' : '#fff',
                border: 'none', borderRadius: 6, padding: '8px 14px',
                fontSize: '0.875rem', cursor: (!filtros.ruta && !filtros.estado && !filtros.cerca && !filtros.fecha) ? 'default' : 'pointer',
                fontWeight: 600, height: 38,
              }}
              disabled={!filtros.ruta && !filtros.estado && !filtros.cerca && !filtros.fecha}
              onClick={() => setFiltros({ fecha: '', ruta: '', estado: '', cerca: '' })}
            >
              Limpiar filtros
            </button>

            <div style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '0.8rem', color: '#9ca3af' }}>
              {generalTab.status === 'loading'
                ? 'Cargando…'
                : `${generalTab.pagination.total} pedido${generalTab.pagination.total !== 1 ? 's' : ''}`}
            </div>
          </div>

          <TablaPedidos filas={general} onAction={handleAccion} />
          <Paginacion
            pagina={generalTab.pagination.page}
            totalPaginas={generalTab.pagination.totalPages}
            total={generalTab.pagination.total}
            onChange={setPaginaGeneral}
          />
        </TabPanel>

        <TabPanel value="historico" sx={{ p: 0 }}>
          <TabContext value={tabHistorico}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <TabList onChange={(_, v) => setTabHistorico(v)} textColor="inherit"
                sx={{ '& .Mui-selected': { color: '#215732 !important' }, '& .MuiTabs-indicator': { background: '#215732' } }}>
                <Tab value="entregados" label={`Entregados (${entregadosTab.pagination.total})`} sx={{ fontSize: '0.8rem' }} />
                <Tab value="rechazados" label={`Rechazados (${rechazadosTab.pagination.total})`} sx={{ fontSize: '0.8rem' }} />
                <Tab value="todos" label={`Todos (${todosTab.pagination.total})`} sx={{ fontSize: '0.8rem' }} />
              </TabList>
              <div className="mb-2">
                <SelectorPorPagina valor={porPaginaHistorico} onChange={n => { setPorPaginaHistorico(n); setPaginaHistorico(1); }} />
              </div>
            </Box>
            <TabPanel value="entregados" sx={{ p: 0 }}>
              <TablaPedidos filas={entregados} onAction={handleAccion} mostrarComprovant />
              <Paginacion pagina={entregadosTab.pagination.page} totalPaginas={entregadosTab.pagination.totalPages} total={entregadosTab.pagination.total} onChange={setPaginaHistorico} />
            </TabPanel>
            <TabPanel value="rechazados" sx={{ p: 0 }}>
              <TablaPedidos filas={rechazados} onAction={handleAccion} />
              <Paginacion pagina={rechazadosTab.pagination.page} totalPaginas={rechazadosTab.pagination.totalPages} total={rechazadosTab.pagination.total} onChange={setPaginaHistorico} />
            </TabPanel>
            <TabPanel value="todos" sx={{ p: 0 }}>
              <TablaPedidos filas={todos} onAction={handleAccion} />
              <Paginacion pagina={todosTab.pagination.page} totalPaginas={todosTab.pagination.totalPages} total={todosTab.pagination.total} onChange={setPaginaHistorico} />
            </TabPanel>
          </TabContext>
        </TabPanel>

        <TabPanel value="rutas" sx={{ p: 0 }}><GestionRutas /></TabPanel>

        <TabPanel value="kpis" sx={{ p: 0 }}><DashboardKpis /></TabPanel>
      </TabContext>

      <ModalDetalle open={modal.tipo === 'detail'} onClose={cerrarModal} pedido={modal.pedido} />
      <ModalConfirmarEntrega open={modal.tipo === 'confirmar'} onClose={cerrarModal} pedido={modal.pedido} onConfirm={handleConfirmar} />
      <ModalProrrogar open={modal.tipo === 'aplazar'} onClose={cerrarModal} pedido={modal.pedido} onConfirm={handleAplazar} />
      <ModalRechazar open={modal.tipo === 'rechazar'} onClose={cerrarModal} pedido={modal.pedido} onConfirm={handleRechazar} />

      <Dialog open={modal.tipo === 'ver-rechazo'} onClose={cerrarModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#C62828' }}>Motivo del rechazo</DialogTitle>
        <DialogContent>
          <p style={{ fontSize: '0.875rem' }}>{modal.pedido?.motiuRebuig || 'Sin motivo registrado'}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarModal} color="inherit" size="small">Cerrar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}